/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContentPermissions from './ContentCards/useContentPermissions';

interface ContentRouteWrapperProps {
	children: React.ReactNode;
	contentType?: 'articles' | 'documents' | 'videos' | 'images' | 'podcasts' | 'playlists' | undefined;
	isCreation?: boolean;
	isEdit?: boolean;
}

// using this wrapper to check if the content type is enabled or not in configuration
// used in edit, create and view pages
const ContentRouteWrapper = (props: ContentRouteWrapperProps) =>
{
	const { children, isCreation, isEdit } = props;

	const { viewType } = useParams();
	const contentType = props?.contentType || viewType?.toLowerCase() as 'articles' | 'documents' | 'videos' | 'images' | 'podcasts' | 'playlists';
	const navigate = useNavigate();

	const {
		isArticlesEnabled,
		isDocumentsEnabled,
		isVideosEnabled,
		isImagesEnabled,
		isPodcastsEnabled,
		isPlaylistsEnabled
	} = useContentPermissions();

	const { hasCreatePermission, hasViewPermission, hasEditPermission } = useContentPermissions();
	const canCreateContent = isCreation && hasCreatePermission;
	const canEditContent = isEdit && hasEditPermission;

	useEffect(() =>
	{
		let isEnabled = false;
		let errorMessage = '';

		// Only check module enablement for create/edit operations
		if (isCreation || isEdit)
		{
			switch (contentType)
			{
				case 'articles':
					isEnabled = isArticlesEnabled;
					errorMessage = 'Articles are not enabled';
					break;
				case 'documents':
					isEnabled = isDocumentsEnabled;
					errorMessage = 'Documents are not enabled';
					break;
				case 'videos':
					isEnabled = isVideosEnabled;
					errorMessage = 'Videos are not enabled';
					break;
				case 'images':
					isEnabled = isImagesEnabled;
					errorMessage = 'Images are not enabled';
					break;
				case 'podcasts':
					isEnabled = isPodcastsEnabled;
					errorMessage = 'Podcasts are not enabled';
					break;
				case 'playlists':
					isEnabled = isPlaylistsEnabled;
					errorMessage = 'Playlists are not enabled';
					break;
				default:
					isEnabled = false;
					errorMessage = 'Content type is not enabled';
			}

			// Block create/edit operations if module is disabled
			if (isCreation ? !canCreateContent : !isEnabled)
			{
				errorMessage = isCreation ? 'You do not have permission to create content' : errorMessage;
				toast.error(errorMessage);
				navigate(hasViewPermission ? '/businessSettings/content' : '/news', { replace: true });
			}
		}
		// For view operations, no module enablement check needed
	}, [ contentType, isArticlesEnabled, isDocumentsEnabled,
		isVideosEnabled, isImagesEnabled, isPodcastsEnabled,
		isPlaylistsEnabled, navigate, hasViewPermission, hasCreatePermission, isCreation, isEdit ]);

	// Only render children if the content type is enabled (for create/edit) or always (for view)
	const getIsEnabled = () =>
	{
		// For view operations, always allow
		if (!isCreation && !isEdit)
		{
			return true;
		}

		// For create/edit operations, check module enablement
		switch (contentType)
		{
			case 'articles': return isCreation ? canCreateContent : canEditContent;
			case 'documents': return isCreation ? canCreateContent : canEditContent;
			case 'videos': return isCreation ? canCreateContent : canEditContent;
			case 'images': return isCreation ? canCreateContent : canEditContent;
			case 'podcasts': return isCreation ? canCreateContent : canEditContent;
			case 'playlists': return isCreation ? canCreateContent : canEditContent;
			default: return false;
		}
	};

	return getIsEnabled() ? <>{children}</> : null;
};

export default ContentRouteWrapper;